import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      // The EMPTY box is neutral in both themes and the CHECKED box is the
      // brand blue in both themes, which is the whole point: this used to
      // borrow `--focus-border`, a token that is deliberately a different
      // colour per theme (navy in light, cyan in dark), so an unticked box
      // read as a hard black outline on a white row and as a blue one on a
      // dark row — the same control apparently made of two different
      // materials. A tint of the muted text colour is the same grey relative
      // to whichever surface it sits on, so the empty state is one object;
      // the fill and its white check are then the only colour the control
      // ever shows, and they mean "ticked".
      "peer h-4 w-4 shrink-0 rounded-sm border border-muted-foreground/70 ring-offset-background transition-[border-color,background-color,box-shadow,filter]",
      // Hover must be visible on every surface the box sits on (white row,
      // dark card, the blue "mine" row): the unticked box takes the ring
      // colour + a soft halo, the ticked box brightens its fill.
      "hover:border-[hsl(var(--ring))] hover:shadow-[0_0_0_2.5px_hsl(var(--ring)/0.2)]",
      "data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:hover:brightness-115",
      "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className={cn("flex items-center justify-center text-current")}>
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
